import type { IDataTable } from '../../types/data_table';

import { getValueFromNestedObject } from '../../utils/getValueFromNestedObject';

import { TableWrapper } from '../Table/TableWrapper';

import { FiltersBar } from './FiltersBar';
import { Header } from './Header';
import { PaginationBar } from './PaginationBar';
import { TableBody } from './TableBody';
import { buildPaginationProps } from './paginationProps';
import { useDataTableFilters } from './useDataTableFilters';
import { useDataTableQuery } from './useDataTableQuery';
import { mergeFiltersLocalization } from './getDefaultFiltersLocalization';

import styles from './data-table.module.scss';

/**
 * A self-fetching table: give it a query function, a column map and an
 * optional filters map, and it owns the rest — filter state, paging, request
 * lifecycle and the loading, empty and error states in between.
 *
 * This component is only the wiring. It holds no state of its own; everything
 * it renders comes from two hooks and one builder:
 *
 * - {@link useDataTableFilters} turns the consumer's `filtersMap` into the
 *   list the bar renders, and into the `params` sent with every request.
 * - {@link useDataTableQuery} owns paging, the request, and the per-region
 *   loading flags.
 * - {@link buildPaginationProps} assembles the pager, or decides there is
 *   nothing to page.
 *
 * See `index.mdx` for the full prop reference and usage examples.
 */
export const DataTable = ({
  lang,
  disabled,
  header,
  filters,
  tableBody,
  pagination,
  queries,
  emptyState,
  errorState,
  additionalFilter,
}: IDataTable) => {
  const {
    tableId,
    dataLocation,
    columnsMap,
    rowClickHandler,
    isLoading: isTableBodyLoading,
  } = tableBody;

  const {
    filtersList,
    params,
    filtersTypes,
    paramsReady,
    filtersBarRenderKey,
    filtersChangeHandler,
    resetFilters,
    filtersLocalization
  } = useDataTableFilters({
    lang,
    filters,
    resetSignal: queries.resetFiltersParams,
  });

  const {
    currentPage,
    pageSize,
    response,
    isError,
    loading,
    handlePageChange,
    handlePageSizeChange,
  } = useDataTableQuery({
    lang,
    queries,
    params,
    filtersTypes,
    paramsReady,
    pageSizeOptions: pagination?.pageSizeOptions,
    // A failed filters request is terminal, so the bar drops its skeleton
    // rather than waiting on a map that is never going to arrive.
    isFiltersLoading: (filters?.isLoading ?? false) && !filters?.isError,
    onPageChange: pagination?.onPageChange,
    onPageSizeChange: pagination?.onPageSizeChange,
    onError: errorState?.onError,
  });

  /** The rows themselves, dug out of the response by `tableBody.dataLocation`. */
  const rows = getValueFromNestedObject(response, dataLocation);

  const paginationProps = buildPaginationProps({
    pagination,
    lang,
    response,
    rows,
    currentPage,
    pageSize,
    isTableLoading: loading.pagination,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  });

  return (
    <TableWrapper disabled={disabled}>
      {header && (
        <div className={styles['data-table__header']}>
          <Header isLoading={loading.header} {...header} />
        </div>
      )}

      {(filters || additionalFilter) && (
        <div className={styles['data-table__filters-bar']}>
          {filters && (
            <FiltersBar
              // Remounting on reset is what clears the bar's own internal
              // state (open menus, half-typed inputs, pending selections).
              key={`filters-bar-${filtersBarRenderKey}`}
              lang={lang}
              filtersList={filtersList}
              filtersChangeHandler={filtersChangeHandler}
              resetFilters={resetFilters}
              isLoading={loading}
              isMobile={filters.isMobile}
              country={filters.country}
              localization={filtersLocalization}
              hasActiveFilters={!!Object.keys(params).length}
            />
          )}
          {additionalFilter && (
            <div
              className={styles['data-table__filters-bar__additional-filter']}
            >
              {additionalFilter}
            </div>
          )}
        </div>
      )}

      <TableBody
        lang={lang}
        columnsMap={columnsMap}
        // `undefined` (rather than an empty list) until the first response, so
        // the body shows its skeleton instead of its empty state.
        data={response ? rows : undefined}
        tableId={tableId}
        rowClickHandler={rowClickHandler}
        isLoading={isTableBodyLoading || loading.table}
        isError={isError}
        emptyState={emptyState}
        // The header already names this table on screen; reuse it so screen
        // readers hear the same name instead of a bare "table".
        ariaLabel={header?.title}
      />

      {paginationProps && <PaginationBar {...paginationProps} />}
    </TableWrapper>
  );
};
